import { ExtractedRecord } from "@/types";

/**
 * Absolute record-index handling for the document review surface (#6755).
 *
 * THE RULE, and it is the only rule in this module: an absolute index is
 * assigned exactly once, at ingress, and every downstream consumer reads it.
 * Nothing recomputes an index from a position in an array it happens to be
 * holding.
 *
 * Why that matters concretely. Three things are expressed in absolute
 * record-index space:
 *
 *   1. The approve payload's `excluded_record_indices`, which the worker
 *      applies as `excludedSet[i]` against the full unsorted record set
 *      (document_loading/workflow.go).
 *   2. The review projection's `extracted_record_index`, on five response
 *      types — claimant forecasts, reimbursement summaries and their rows,
 *      and claim-item forecasts.
 *   3. `document_transformation_records.record_index` itself, which is the
 *      identity half of (transformation_id, record_index).
 *
 * Position in a JS array equals that only for a complete, gap-free read. It
 * diverges the moment the set is windowed, deduplicated, sorted, or filtered —
 * and the review surface does all four. A recomputed index silently becomes a
 * post-filter ordinal, so a reviewer excluding one row removes a DIFFERENT
 * claim line, and a projection lookup attaches the wrong claimant's forecast.
 * Neither failure is visible at the call site or on screen.
 *
 * See migration 000207's `record_index` column comment for the full reasoning.
 */

/** An extracted record as the windowed endpoint returns it. */
export interface IndexedExtractedRecord {
  record_index: number;
  record: ExtractedRecord;
}

/**
 * Stamp absolute indices onto a WINDOWED read.
 *
 * This is the ONLY stamper. A full-set variant that derived the index from
 * array position existed while `?expand=extracted_records` did; that read is
 * gone from the SPA, so the precondition making position-derivation correct
 * (a complete, gap-free, in-order set) is no longer satisfiable anywhere —
 * and small documents come through this same windowed endpoint anyway.
 *
 * The index comes from the server's `record_index`, which it SELECTs from the
 * stored column rather than inferring from result position — see
 * `GetTransformationRecords` and `IndexedRecord` in the repository layer. This
 * function therefore never computes anything; it only moves the value onto the
 * record so downstream consumers have one shape to read.
 */
export const stampAbsoluteIndicesFromWindow = (page: IndexedExtractedRecord[]): ExtractedRecord[] =>
  page.map(({ record_index, record }) => ({ ...record, record_index }));

/**
 * Read a record's absolute index.
 *
 * Returns `undefined` when the record was never stamped, deliberately: every
 * caller then declines to act rather than falling back to a position-derived
 * value. A fallback is precisely the bug — it would produce a plausible index
 * pointing at the wrong record, which is worse than producing none.
 */
export const absoluteIndexOf = (record: ExtractedRecord): number | undefined => record.record_index;

/**
 * Absolute indices for a set of records, dropping any that were never
 * stamped.
 *
 * Used for the approve payload. An unstamped record is omitted rather than
 * guessed at: omitting it means a record the reviewer excluded still loads,
 * which is visible and recoverable. Guessing means a record they did NOT
 * exclude is dropped instead, silently, and the reviewer has no way to tell.
 */
export const absoluteIndicesOf = (records: Iterable<ExtractedRecord>): number[] => {
  const out: number[] = [];
  for (const record of records) {
    const index = absoluteIndexOf(record);
    if (index !== undefined) out.push(index);
  }
  return out.sort((a, b) => a - b);
};

/**
 * Set key for per-row precomputes over the record grid.
 *
 * ABSOLUTE record_index when the record has one, the object otherwise.
 *
 * Object identity alone is what broke the expand chevron under the errored
 * filter (!4682 review): the precompute was built from one array and the grid
 * rendered another, holding equal-but-distinct objects from a separate server
 * read, so every lookup missed. Keying on the index makes such a precompute
 * survive any re-fetch or windowed read — the same move useRecordExclusions
 * made away from identity keying, for the same reason its docblock gives.
 *
 * The identity fallback keeps the key TOTAL. A projection-derived row can
 * legitimately carry no record_index, and returning `undefined` for those
 * would collapse every one of them onto a single key — which is worse than
 * identity, because it would make them all expandable as soon as any one of
 * them was.
 */
export const expandableRowKey = (record: ExtractedRecord): number | ExtractedRecord =>
  absoluteIndexOf(record) ?? record;

/**
 * The synthetic `id` CompactTable requires, derived DETERMINISTICALLY.
 *
 * `ExtractedRecord` has no `id`, and CompactTable keys selection and expand
 * state on one. Two separate assigners used to exist — `asRow` in
 * RecordReviewGrid and `asNestedRow` in ExpandedRecordDetails — each with its
 * own identity WeakMap and counter, and BOTH mutating the record via
 * `Object.assign(record, { id })`.
 *
 * That is a bug, not just duplication, because the two tables share record
 * objects. A claims-register row carries both `claimant` and `claim_item`
 * fields, so a claimant's linked-records set contains the claimant itself —
 * the parent row appears in its own nested table. Rendering the nested table
 * therefore OVERWROTE the parent's id:
 *
 *   1. outer row stamped `record_0`, reviewer expands it -> expanded={record_0}
 *   2. nested table renders, re-stamps the same object `nested_0`
 *   3. no re-render yet, so the panel stays open under a stale id
 *   4. next click reads `nested_0` and ADDS it -> still expanded, click looks dead
 *   5. click after that removes it -> closes
 *
 * Which is precisely the "two clicks to close, but only the first time"
 * symptom reported on the reimbursement tab (!4682 review follow-up),
 * reproduced from the console: clicked `idx_0`, then `nested_0` twice.
 *
 * Deriving the id from the record's own absolute index removes the failure
 * mode rather than coordinating the two assigners: the same record gets the
 * same id from either table, so a re-stamp is a no-op. The tables keep
 * SEPARATE expand state (it is component state in each CompactTable), so
 * sharing an id value across them is harmless.
 *
 * Keyed on the index rather than object identity for the second reason too —
 * any re-fetch or windowed read produces fresh objects, which silently drops
 * expand and selection state. Same reasoning as useRecordExclusions (#6755).
 *
 * TWO ID SPACES, prefixed apart. `idx_<n>` when the record has an absolute
 * index, `obj_<n>` when it does not (projection-derived rows can legitimately
 * have none). An unprefixed index and an unprefixed counter would collide, and
 * a collision means two unrelated rows share state — worse than the bug.
 */
const fallbackIdMap = new WeakMap<ExtractedRecord, string>();
let nextFallbackId = 0;

export const gridRowId = (record: ExtractedRecord): string => {
  const index = absoluteIndexOf(record);
  if (index !== undefined) return `idx_${index}`;

  let id = fallbackIdMap.get(record);
  if (id === undefined) {
    id = `obj_${nextFallbackId++}`;
    fallbackIdMap.set(record, id);
  }
  return id;
};
